import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { Avatar, Spinner } from "../../components/lawyer/LawyerUi";
import StripeCardModal from "../../components/user/StripeCardModal";
import {
  Attachment,
  CallCard,
  ReplyQuote,
  fileNameOf,
  fileTypeOf,
  mergeMessages,
  pickRecorderMime,
  replyIcon,
  snippetOf,
} from "../lawyer/ChatConversation";
import { apiErrorMessage, userService } from "../../services/userApi";
import socketService from "../../services/SocketService";

const money = (v) => `$ ${Number((Number(v) || 0).toFixed(2)).toLocaleString()}`;

// What the client is charged for an invoice: the lawyer's total plus any VAT.
const invoiceDue = (inv) => Number(inv?.grand_total ?? inv?.total_amount) || 0;

/* ---------------- Message pieces ---------------- */

const InvoiceCard = ({ message, onPay }) => {
  const inv = message.invoice_data || {};
  const paid = inv.pay_status === "Paid";
  const done = inv.task_status === "Completed";
  const vat = Number(inv.vat_amount) || 0;
  return (
    <div className="lwp-invoice">
      <div className="lwp-invoice-top">
        <span className="lwp-invoice-no">Invoice: #{message.id}</span>
        <span className={`lwp-status ${paid ? "lwp-status-ended" : "lwp-status-pending"}`}>{paid ? "Paid" : "Pending"}</span>
      </div>
      {inv.title && <h4>{inv.title}</h4>}
      {Array.isArray(inv.items) && inv.items.length > 0 && (
        <ul className="lwp-invoice-items">
          {inv.items.map((item, i) => (
            <li key={i}>
              <span>{item.title}</span>
              <span>{money(item.amount)}</span>
            </li>
          ))}
          {vat > 0 && (
            <li>
              <span>VAT</span>
              <span>{money(vat)}</span>
            </li>
          )}
        </ul>
      )}
      <div className="lwp-invoice-total">
        <strong>Total</strong>
        <span>{money(paid ? inv.total_amount : invoiceDue(inv))}</span>
      </div>
      <div className="lwp-invoice-foot">
        {!paid ? (
          <button type="button" className="lwp-btn lwp-btn-sm" onClick={() => onPay(message)}>
            <i className="bi bi-credit-card me-2" /> Pay Now
          </button>
        ) : (
          done && <span className="lwp-status lwp-status-ended">Task Completed</span>
        )}
      </div>
    </div>
  );
};

const MessageBubble = ({ message, mine, avatar, lawyerName, onPay, onReply, onJumpTo }) => {
  const text = (message.message || "").trim();
  // File-only messages carry the file name as text (the API needs a body); don't repeat it.
  const showText = text && !(message.file && text === fileNameOf(message.file)) && !message.invoice_data;
  const time = moment(message.created_at);
  const reply = message.reply_to;

  return (
    <div className={`lwp-msg${mine ? " mine" : ""}`} id={`lwp-msg-${message.id}`}>
      {!mine && <Avatar src={avatar} size={44} className="lwp-msg-avatar" />}
      <div className="lwp-msg-col">
        <div className="lwp-msg-line">
          {message.call_data ? (
            <CallCard message={message} />
          ) : message.invoice_data ? (
            <div className="lwp-msg-stack">
              {text && text !== "Invoice" && <div className="lwp-bubble">{text}</div>}
              <InvoiceCard message={message} onPay={onPay} />
            </div>
          ) : (
            <div className={`lwp-bubble${message.file_type === "image" || message.file_type === "video" ? " has-media" : ""}`}>
              {reply && (
                <ReplyQuote reply={reply} author={reply.sender === "user" ? "You" : lawyerName} onClick={() => onJumpTo(reply.id)} />
              )}
              <Attachment message={message} />
              {showText && <p className="lwp-bubble-text">{text}</p>}
            </div>
          )}
          {!message.call_data && !message.invoice_data && (
            <button type="button" className="lwp-reply-btn" onClick={() => onReply(message)} aria-label="Reply" title="Reply">
              <i className="bi bi-reply-fill" />
            </button>
          )}
        </div>
        <span className="lwp-msg-meta">
          {mine && !message.call_data && (
            <i
              className={`bi ${Number(message.is_read) === 1 ? "bi-check2-all read" : "bi-check2"}`}
              aria-label={Number(message.is_read) === 1 ? "Read" : "Sent"}
            />
          )}
          {time.isValid() ? time.format("hh:mm A") : ""}
        </span>
      </div>
      {mine && <Avatar src={avatar} size={44} className="lwp-msg-avatar" />}
    </div>
  );
};

/* ---------------- Conversation ---------------- */

// The client's side of a chat with a lawyer. Same UI as the lawyer portal's
// conversation, but messages go out as sender "user" and invoices are paid here.
const UserChatConversation = ({ chatId, onBack, onActivity }) => {
  const { profile } = useOutletContext();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState(false);
  const [typing, setTyping] = useState(false);
  const [recording, setRecording] = useState(null); // { seconds }
  const [payInvoice, setPayInvoice] = useState(null);

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const stickToBottom = useRef(true);
  const prevScrollHeight = useRef(null);
  const typingTimer = useRef(null);
  const recorderRef = useRef(null);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const userId = profile?.id;
  const lawyerId = chat?.lawyer_id;
  const lawyerName = chat?.lawyer?.name || "Lawyer";
  const socketChat = useCallback(
    () => ({ id: Number(chatId), user_id: userId, lawyer_id: lawyerId }),
    [chatId, userId, lawyerId]
  );

  /* ----- loading ----- */

  const loadPage = useCallback(
    async (nextPage) => {
      const res = await userService.getChatMessages(chatId, nextPage);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const data = res.data.data || {};
      if (data.chat) setChat(data.chat);
      setMessages((prev) => mergeMessages(nextPage === 1 ? [] : prev, data.messages || []));
      setPage(data.pagination?.current_page || nextPage);
      setLastPage(data.pagination?.last_page || 1);
      return data;
    },
    [chatId]
  );

  useEffect(() => {
    let active = true;
    stickToBottom.current = true;
    loadPage(1)
      .catch((err) => active && toast.error(apiErrorMessage(err, err?.message || "Unable to load messages.")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [loadPage]);

  // Opening the chat marks the lawyer's messages read (server side); tell them live.
  useEffect(() => {
    if (!userId || !lawyerId) return;
    socketService.getSocket()?.emit("message-read", { reader: "user" }, socketChat());
    onActivityRef.current?.();
  }, [userId, lawyerId, socketChat]);

  const loadOlder = async () => {
    if (loadingOlder || page >= lastPage) return;
    setLoadingOlder(true);
    prevScrollHeight.current = scrollRef.current?.scrollHeight || null;
    try {
      await loadPage(page + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoadingOlder(false);
    }
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prevScrollHeight.current !== null) {
      el.scrollTop = el.scrollHeight - prevScrollHeight.current;
      prevScrollHeight.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, typing]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 60) loadOlder();
  };

  /* ----- realtime ----- */

  useEffect(() => {
    if (!lawyerId) return undefined;
    const sameChat = (c) => c && String(c.lawyer_id) === String(lawyerId);
    const presence = { type: "lawyer", id: lawyerId };
    const upsert = (message) => {
      if (String(message?.chat_id) === String(chatId)) setMessages((prev) => mergeMessages(prev, [message]));
    };

    const offs = [
      socketService.subscribe("receive-message", (message) => {
        if (String(message?.chat_id) !== String(chatId)) return;
        stickToBottom.current = true;
        setTyping(false);
        setMessages((prev) => mergeMessages(prev, [message]));
        userService.readChatMessages(chatId).catch(() => {});
        socketService.getSocket()?.emit("message-read", { reader: "user" }, socketChat());
      }),
      socketService.subscribe("message-read", (data, c) => {
        if (data?.reader === "lawyer" && sameChat(c)) {
          setMessages((prev) => prev.map((m) => (m.sender === "user" ? { ...m, is_read: 1 } : m)));
        }
      }),
      socketService.subscribe("typing", (data, c) => sameChat(c) && data?.sender === "lawyer" && setTyping(true)),
      socketService.subscribe("stop-typing", (data, c) => sameChat(c) && setTyping(false)),
      socketService.subscribe("invoice-updated", upsert),
      socketService.subscribe("task-completed", upsert),
      socketService.subscribe("service-completed", (data, c) => {
        if (String(c?.id) === String(chatId)) {
          setChat((prev) => (prev?.user_service ? { ...prev, user_service: { ...prev.user_service, status: "Completed" } } : prev));
        }
      }),
      socketService.subscribe("presence-update", (p) => {
        if (p?.type === "lawyer" && String(p.id) === String(lawyerId)) setOnline(!!p.online);
      }),
    ];
    socketService.emit("watch-presence", presence);

    return () => {
      offs.forEach((off) => off());
      socketService.emit("unwatch-presence", presence);
    };
  }, [chatId, lawyerId, socketChat]);

  const emitTyping = () => {
    if (!lawyerId) return;
    const sock = socketService.getSocket();
    if (!typingTimer.current) sock?.emit("typing", { sender: "user" }, socketChat());
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      sock?.emit("stop-typing", { sender: "user" }, socketChat());
      typingTimer.current = null;
    }, 1500);
  };

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  /* ----- sending ----- */

  const deliver = (message) => {
    stickToBottom.current = true;
    setMessages((prev) => mergeMessages(prev, [message]));
    socketService.getSocket()?.emit("send-message", message, socketChat());
    onActivityRef.current?.(message);
  };

  const send = async (payload) => {
    setSending(true);
    try {
      const res = await userService.sendMessage({ chat_id: chatId, reply_to_id: replyTo?.id, ...payload });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      deliver(res.data.data);
      setReplyTo(null);
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Message not sent."));
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (sending || (!body && !pendingFile)) return;

    let ok;
    if (pendingFile) {
      const cleanName = pendingFile.name.replace(/\s+/g, "");
      // The API requires a text body alongside files; fall back to the file name.
      ok = await send({ file: pendingFile, file_type: fileTypeOf(pendingFile), message: body || cleanName });
    } else {
      ok = await send({ message: body });
    }
    if (ok) {
      setText("");
      setPendingFile(null);
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
        typingTimer.current = null;
        socketService.getSocket()?.emit("stop-typing", { sender: "user" }, socketChat());
      }
      inputRef.current?.focus();
    }
  };

  const startReply = (message) => {
    setReplyTo(message);
    inputRef.current?.focus();
  };

  const jumpTo = (id) => {
    const el = document.getElementById(`lwp-msg-${id}`);
    if (!el) {
      toast.info("That message is further back. Scroll up to load older messages.");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("lwp-msg-flash");
    void el.offsetWidth;
    el.classList.add("lwp-msg-flash");
    setTimeout(() => el.classList.remove("lwp-msg-flash"), 1600);
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Files must be 20 MB or smaller.");
      return;
    }
    setPendingFile(file);
  };

  /* ----- invoices ----- */

  const invoicePaid = async (pi) => {
    const res = await userService.invoicePaid(payInvoice.id, pi.id);
    if (!res?.data?.status) throw new Error(res?.data?.message || "Payment received but the invoice wasn't updated. Please contact support.");
    const updated = { ...payInvoice, ...res.data.data };
    setMessages((prev) => mergeMessages(prev, [updated]));
    socketService.getSocket()?.emit("invoice-paid", updated, socketChat());
    toast.success(res.data.message || "Invoice paid successfully");
    return true;
  };

  /* ----- voice notes ----- */

  const startRecording = async () => {
    const mime = pickRecorderMime();
    if (mime === null || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice messages aren't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      const started = Date.now();
      const tick = setInterval(() => setRecording({ seconds: Math.floor((Date.now() - started) / 1000) }), 500);
      recorderRef.current = { recorder, stream, chunks, tick, cancelled: false };
      recorder.onstop = async () => {
        clearInterval(tick);
        stream.getTracks().forEach((t) => t.stop());
        const { cancelled } = recorderRef.current || {};
        recorderRef.current = null;
        setRecording(null);
        if (cancelled || !chunks.length) return;
        const type = recorder.mimeType || mime || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : "webm";
        const name = `voice_${Date.now()}.${ext}`;
        await send({ file: new File(chunks, name, { type }), file_type: "audio", message: name });
      };
      recorder.start();
      setRecording({ seconds: 0 });
    } catch {
      toast.error("Microphone access was blocked.");
    }
  };

  const stopRecording = (cancel = false) => {
    const rec = recorderRef.current;
    if (!rec) return;
    rec.cancelled = cancel;
    rec.recorder.stop();
  };

  useEffect(
    () => () => {
      const rec = recorderRef.current;
      if (rec) {
        rec.cancelled = true;
        rec.recorder.state !== "inactive" && rec.recorder.stop();
      }
    },
    []
  );

  /* ----- render ----- */

  const service = chat?.user_service || null;
  const serviceActive = !service || service.status === "Active";
  const status = typing ? "typing..." : online ? "Online" : "Offline";
  const canSend = !!text.trim() || !!pendingFile;
  let lastDay = null;

  return (
    <div className={`lwp-convo${service && !serviceActive ? " readonly" : ""}`}>
      <header className="lwp-convo-head">
        <button type="button" className="lwp-back lwp-convo-back" onClick={onBack} aria-label="Back to chats">
          <i className="bi bi-chevron-left" />
        </button>
        <Avatar src={chat?.lawyer?.picture} alt={lawyerName} size={44} className="lwp-convo-avatar" />
        <div className="lwp-convo-title">
          <h2>{chat ? lawyerName : " "}</h2>
          <span className={online || typing ? "is-online" : ""}>{chat ? status : ""}</span>
        </div>
      </header>

      <div className="lwp-convo-body" ref={scrollRef} onScroll={onScroll}>
        {loading ? (
          <Spinner />
        ) : (
          <>
            {loadingOlder && <Spinner />}
            {messages.length === 0 && <p className="lwp-convo-hint">Say hello to start the conversation.</p>}
            {messages.map((m) => {
              const day = moment(m.created_at).format("YYYY-MM-DD");
              const divider = day !== lastDay;
              lastDay = day;
              // Call records carry no sender; the client placed the call, so they sit on our side.
              const mine = m.sender === "user" || (!m.sender && !!m.call_data);
              return (
                <React.Fragment key={m.id}>
                  {divider && (
                    <div className="lwp-day-divider">
                      <span>{moment(m.created_at).format("DD MMM, YYYY")}</span>
                    </div>
                  )}
                  <MessageBubble
                    message={m}
                    mine={mine}
                    avatar={mine ? profile?.picture : chat?.lawyer?.picture}
                    lawyerName={lawyerName}
                    onPay={setPayInvoice}
                    onReply={startReply}
                    onJumpTo={jumpTo}
                  />
                </React.Fragment>
              );
            })}
            {typing && (
              <div className="lwp-msg">
                <Avatar src={chat?.lawyer?.picture} size={44} className="lwp-msg-avatar" />
                <div className="lwp-bubble lwp-typing" aria-label="Typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {service && !serviceActive ? (
        <div className="lwp-composer">
          <div className="lwp-service-closed">
            <i className="bi bi-info-circle" />
            <span>{service.status === "Completed" ? "Service has been Completed" : `Service is ${service.status || "inactive"}`}</span>
          </div>
        </div>
      ) : (
        <form className="lwp-composer" onSubmit={handleSubmit}>
          {replyTo && (
            <div className="lwp-reply-preview">
              <i className="bi bi-reply-fill" />
              <div>
                <strong>Replying to {replyTo.sender === "user" ? "yourself" : lawyerName}</strong>
                <span>
                  {replyIcon[replyTo.file_type] && <i className={`bi ${replyIcon[replyTo.file_type]}`} />} {snippetOf(replyTo)}
                </span>
              </div>
              <button type="button" className="lwp-icon-btn" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}
          {pendingFile && (
            <div className="lwp-pending-file">
              <i className={`bi ${fileTypeOf(pendingFile) === "image" ? "bi-image" : "bi-paperclip"}`} />
              <span>{pendingFile.name}</span>
              <button type="button" className="lwp-icon-btn" onClick={() => setPendingFile(null)} aria-label="Remove file">
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}
          <div className="lwp-composer-bar">
            {recording ? (
              <>
                <button type="button" className="lwp-icon-btn lwp-composer-icon" onClick={() => stopRecording(true)} aria-label="Cancel recording">
                  <i className="bi bi-trash3" />
                </button>
                <span className="lwp-recording">
                  <span className="lwp-rec-dot" />
                  Recording {moment.utc(recording.seconds * 1000).format("mm:ss")}
                </span>
                <button type="button" className="lwp-send-btn" onClick={() => stopRecording(false)} aria-label="Send voice message">
                  <i className="bi bi-send-fill" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="lwp-icon-btn lwp-composer-icon"
                  onClick={() => fileRef.current?.click()}
                  aria-label="Attach file"
                  disabled={sending}
                >
                  <i className="bi bi-paperclip" />
                </button>
                <input ref={fileRef} type="file" hidden onChange={pickFile} />
                <textarea
                  ref={inputRef}
                  rows={1}
                  className="lwp-composer-input"
                  placeholder="Type a message..."
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    emitTyping();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && replyTo) {
                      setReplyTo(null);
                    } else if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                {canSend ? (
                  <button type="submit" className="lwp-send-btn" disabled={sending} aria-label="Send">
                    {sending ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-send-fill" />}
                  </button>
                ) : (
                  <button type="button" className="lwp-send-btn" onClick={startRecording} disabled={sending} aria-label="Record voice message">
                    <i className="bi bi-mic-fill" />
                  </button>
                )}
              </>
            )}
          </div>
        </form>
      )}

      <StripeCardModal
        open={!!payInvoice}
        onClose={() => setPayInvoice(null)}
        title="Pay Invoice"
        amountLabel={payInvoice ? money(invoiceDue(payInvoice.invoice_data)) : ""}
        submitLabel={payInvoice ? `Pay ${money(invoiceDue(payInvoice.invoice_data))}` : "Pay"}
        billing={{ name: profile?.name, email: profile?.email }}
        prepare={() => userService.invoicePayment(invoiceDue(payInvoice.invoice_data))}
        onPaid={invoicePaid}
      />
    </div>
  );
};

export default UserChatConversation;
